// Small inline "CO" badge that appears next to an item's name when one
// or more Change Orders affect it. Click → popover with CO history.
//
// Most rows show no badge. The badge surfaces an otherwise-invisible
// audit fact (this item's specs / price / qty has been revised since
// approval) without claiming a dedicated column.
//
// Input: changeOrders — already filtered to this item via
//   procurementRowState (utils.js walks change_orders.field_changes[]
//   for itemId membership).

import React, { useState, useRef, useEffect } from 'react';

function fmtDate(d) { return (d || '').slice(0, 10) || '—'; }
function fmtMoney(v) {
  var n = parseFloat(v) || 0;
  var s = '$' + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? '−' + s : s;
}

var STATUS_COLOR = {
  approved:    '#1A5C38',
  pending:     '#BF6800',
  rejected:    '#888',
  pushed:      '#1A5C38',
  push_failed: '#C44',
  void:        '#888',
};

export default function LedgerCoBadge(p) {
  var cos = p.changeOrders || [];
  var ps = useState(false); var open = ps[0]; var setOpen = ps[1];
  var ref = useRef(null);

  // Close popover on outside click
  useEffect(function() {
    if (!open) return;
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return function() { window.removeEventListener('mousedown', onClick); };
  }, [open]);

  if (cos.length === 0) return null;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={function(e) { e.stopPropagation(); setOpen(function(o) { return !o; }); }}
        title={cos.length + ' Change Order' + (cos.length === 1 ? '' : 's') + ' on this item'}
        style={{
          fontSize: 8, fontWeight: 600, padding: '1px 5px',
          background: 'none', color: '#BF6800',
          border: '0.5px solid #BF6800', borderRadius: 3,
          cursor: 'pointer', fontFamily: 'inherit',
          letterSpacing: '0.06em', textTransform: 'uppercase',
          marginLeft: 6, verticalAlign: 'middle',
        }}
      >
        CO {cos.length > 1 ? ('×' + cos.length) : ''}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          minWidth: 280, maxWidth: 380, padding: 10,
          background: '#fff', border: '0.5px solid #D8D7D2', borderRadius: 4,
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 20,
          fontSize: 11, color: '#222',
        }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: '#888',
            textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
            Change Orders ({cos.length})
          </div>
          {cos.map(function(co) {
            var statusColor = STATUS_COLOR[co.status] || '#888';
            var delta = parseFloat(co.deltaAmount || co.delta_amount) || 0;
            var impact = co.accountingImpact != null ? co.accountingImpact : co.accounting_impact;
            return (
              <div key={co.id} style={{ paddingTop: 8, marginTop: 8, borderTop: '0.5px solid #EFEFEC' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {co.coRef || co.co_ref || '—'}
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 600, color: statusColor,
                    textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {co.status}
                  </span>
                </div>
                {co.reason && (
                  <div style={{ fontSize: 11, color: '#444', marginTop: 4, lineHeight: 1.4 }}>
                    {co.reason}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: '#888' }}>
                  <span>Filed {fmtDate(co.filedAt || co.filed_at)}</span>
                  {(co.approvedAt || co.approved_at) && <span>Approved {fmtDate(co.approvedAt || co.approved_at)}</span>}
                </div>
                {delta !== 0 && (
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    Δ <span style={{ fontFamily: 'monospace', fontWeight: 500,
                      color: delta > 0 ? '#D4007A' : '#1A5C38' }}>{fmtMoney(delta)}</span>
                    {impact === false && (
                      <span style={{ fontSize: 9, color: '#888', marginLeft: 6, fontStyle: 'italic' }}>
                        spec-only (absorbed)
                      </span>
                    )}
                    {impact === true && (
                      <span style={{ fontSize: 9, color: '#BF6800', marginLeft: 6, fontStyle: 'italic' }}>
                        billed
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}
